import React from 'react';
import { AlertTriangle, Clock, TrendingUp } from 'lucide-react';
import type { KPIBarData } from '../services/yamazumiSyncService';

interface KPIBarProps {
  data: KPIBarData;
  isLoading?: boolean;
  className?: string;
}

interface KPIChipProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: 'ok' | 'warning' | 'critical' | 'neutral';
  title?: string;
}

const TONE_CLASSES: Record<KPIChipProps['tone'], string> = {
  ok: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  warning: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  critical: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
  neutral: 'border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-secondary)]',
};

function KPIChip({ icon, label, value, tone, title }: KPIChipProps) {
  return (
    <div
      className={`flex items-center gap-1.5 rounded-[4px] border px-2.5 py-1.5 ${TONE_CLASSES[tone]}`}
      title={title}
    >
      <span className="shrink-0 opacity-80">{icon}</span>
      <div className="flex flex-col leading-tight">
        <span className="text-[8px] uppercase tracking-[0.08em] opacity-70">{label}</span>
        <span className="font-mono text-[11px] font-bold">{value}</span>
      </div>
    </div>
  );
}

function eficienciaTone(ef: number | null): KPIChipProps['tone'] {
  if (ef === null) return 'neutral';
  if (ef >= 0.95) return 'ok';
  if (ef >= 0.80) return 'warning';
  return 'critical';
}

function gargaloTone(g: number): KPIChipProps['tone'] {
  if (g === 0) return 'ok';
  if (g <= 2) return 'warning';
  return 'critical';
}

/**
 * KPIBar — barra horizontal com 3 KPIs operacionais: Eficiência, Gargalos, Fila Média.
 * Alimentada por `calcularKPIs()` de yamazumiSyncService.
 */
export function KPIBar({ data, isLoading = false, className = '' }: KPIBarProps) {
  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 border-b border-[var(--border-default)] bg-[var(--bg-elevated)] px-3 py-1.5 ${className}`}>
        <div className="h-7 w-24 animate-pulse rounded bg-[var(--bg-surface)]" />
        <div className="h-7 w-24 animate-pulse rounded bg-[var(--bg-surface)]" />
        <div className="h-7 w-28 animate-pulse rounded bg-[var(--bg-surface)]" />
      </div>
    );
  }

  if (data.measuredTasks === 0) return null;

  const efValue = data.eficiencia !== null
    ? `${Math.round(data.eficiencia * 100)}%`
    : '—';

  const filaValue = data.filaMedia !== null
    ? `${data.filaMedia} min`
    : '—';

  return (
    <div
      className={`flex items-center gap-2 border-b border-[var(--border-default)] bg-[var(--bg-elevated)] px-3 py-1.5 ${className}`}
      role="region"
      aria-label="KPIs operacionais"
    >
      <span className="mr-1 text-[8px] uppercase tracking-[0.1em] text-[var(--text-muted)]">
        Real ({data.measuredTasks} tarefas)
      </span>

      <KPIChip
        icon={<TrendingUp size={11} />}
        label="Eficiência"
        value={efValue}
        tone={eficienciaTone(data.eficiencia)}
        title="sum(planejado) / sum(real)"
      />
      <KPIChip
        icon={<AlertTriangle size={11} />}
        label="Gargalos"
        value={String(data.gargalos)}
        tone={gargaloTone(data.gargalos)}
        title="Tarefas onde tempo real > planejado"
      />
      <KPIChip
        icon={<Clock size={11} />}
        label="Fila média"
        value={filaValue}
        tone="neutral"
        title="Tempo médio de espera antes de iniciar"
      />
    </div>
  );
}
